/**
 * 모바일 코드 인터셉터 유틸리티
 * UC-02 Phase 1.5: 런타임 라이브러리 차단 메커니즘
 * 
 * @author GlucoSOUND Team
 * @since 2025-01-22
 */

import { API_BASE_URL } from './common';

class MobileCodeInterceptor {
    /**
     * 라이브러리 사용 검증
     * 
     * @param {string} libraryName 라이브러리 이름
     * @param {string|null} version 라이브러리 버전
     * @param {Object} additionalContext 추가 컨텍스트
     * @returns {Promise<boolean>} 허용 여부
     */
    static async validateLibrary(libraryName, version = null, additionalContext = {}) {
        try {
            // 캐시된 결과 확인
            const cachedResult = this.getCachedValidation(libraryName, version);
            if (cachedResult !== null) {
                return cachedResult;
            }

            // 관리자 프론트엔드는 쿠키 기반 인증 사용
            // JWT 토큰은 HttpOnly 쿠키로 자동 전송됨

            const context = {
                page: window.location.pathname,
                timestamp: new Date().toISOString(),
                user_agent: navigator.userAgent,
                referrer: document.referrer,
                ...additionalContext
            };

            //console.log(`🔍 모바일 코드 검증 시작: ${libraryName}`, { version, context });
            
            const response = await fetch(`${API_BASE_URL}/mobile-code-policies/validate-library-usage`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                credentials: 'include', // 쿠키 포함하여 전송
                body: JSON.stringify({
                    library_name: libraryName,
                    version: version,
                    context: context
                })
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
                } else if (response.status === 403) {
                    const errorData = await response.json();
                    throw new Error(`권한이 없습니다: ${errorData.message || '접근 거부'}`);
                } else {
                    throw new Error(`서버 오류: ${response.status} ${response.statusText}`);
                }
            }
            
            const result = await response.json();
            
            if (!result.success) {
                throw new Error(`API 오류: ${result.message || '알 수 없는 오류'}`);
            }

            const validationData = result.data;
            
            if (!validationData.allowed) {
                const errorMsg = `🚨 모바일 코드 차단: ${libraryName} 라이브러리가 보안 정책에 의해 차단되었습니다.\n\n` +
                              `사유: ${validationData.reason}\n` +
                              `보안 레벨: ${validationData.security_level}\n` +
                              `정책 ID: ${validationData.policy_id || 'N/A'}`;
                
                console.error('🚫 Mobile code blocked:', validationData);
                
                // 사용자에게 친화적인 알림 표시
                this.showBlockedLibraryNotification(libraryName, validationData);
                
                throw new Error(errorMsg);
            }
            
            //console.log(`✅ Mobile code allowed: ${libraryName}`, validationData);
            
            // 성공 결과를 캐시에 저장
            this.setCachedValidation(libraryName, version, true);
            return true;
            
        } catch (error) {
            console.error('❌ Mobile code validation failed:', error);
            
            // 네트워크 오류나 서버 오류의 경우 사용자에게 알림
            if (error.message.includes('fetch') || error.message.includes('서버')) {
                this.showNetworkErrorNotification(libraryName, error);
            }
            
            throw error;
        }
    }
    
    /**
     * 라이브러리 이름 추출 (모듈 경로에서)
     * 
     * @param {string} specifier 모듈 지정자
     * @returns {string} 라이브러리 이름
     */
    static extractLibraryName(specifier) {
        // 알려진 라이브러리 패턴 매칭
        const knownLibraries = {
            'chart.js': /chart\.js/i,
            'jspdf': /jspdf/i,
            'html2canvas': /html2canvas/i,
            'react': /^react$/i,
            'lodash': /^lodash$/i,
            'axios': /^axios$/i,
            'moment': /moment/i,
            'bootstrap': /bootstrap/i,
            'jquery': /jquery/i
        };
        
        for (const [libName, pattern] of Object.entries(knownLibraries)) {
            if (pattern.test(specifier)) {
                return libName;
            }
        }
        
        // npm 패키지명 추출 (스코프 패키지 포함)
        const npmMatch = specifier.match(/^(@[^\/]+\/[^\/]+|[^\/]+)/);
        if (npmMatch) {
            return npmMatch[1];
        }
        
        // URL에서 라이브러리명 추출
        const urlMatch = specifier.match(/\/([^\/]+)\.(?:js|min\.js)$/);
        if (urlMatch) {
            return urlMatch[1];
        }
        
        // 기본적으로 첫 번째 경로 세그먼트 사용
        const pathMatch = specifier.match(/([^\/\?#]+)/);
        return pathMatch ? pathMatch[1] : specifier;
    }

    /**
     * 차단된 라이브러리 알림 표시
     * 
     * @param {string} libraryName 라이브러리 이름
     * @param {Object} validationData 검증 결과 데이터
     */
    static showBlockedLibraryNotification(libraryName, validationData) {
        // 기존 알림이 있으면 제거
        const existingAlert = document.getElementById('mobile-code-blocked-alert');
        if (existingAlert) {
            existingAlert.remove();
        }

        const alertDiv = document.createElement('div');
        alertDiv.id = 'mobile-code-blocked-alert';
        alertDiv.className = 'alert alert-danger alert-dismissible fade show position-fixed';
        alertDiv.style.cssText = `
            top: 20px;
            right: 20px;
            z-index: 9999;
            max-width: 400px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        `;
        
        alertDiv.innerHTML = `
            <div class="d-flex align-items-start">
                <i class="fas fa-shield-alt text-danger me-2 mt-1"></i>
                <div class="flex-grow-1">
                    <h6 class="alert-heading mb-1">🚨 보안 정책 위반</h6>
                    <p class="mb-1"><strong>${libraryName}</strong> 라이브러리가 차단되었습니다.</p>
                    <small class="text-muted">
                        사유: ${validationData.reason}<br>
                        보안 레벨: ${validationData.security_level}
                    </small>
                </div>
                <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
            </div>
        `;
        
        document.body.appendChild(alertDiv);
        
        // 10초 후 자동 제거
        setTimeout(() => {
            if (alertDiv.parentNode) {
                alertDiv.remove();
            }
        }, 10000);
    }

    /**
     * 네트워크 오류 알림 표시
     * 
     * @param {string} libraryName 라이브러리 이름
     * @param {Error} error 오류 객체
     */
    static showNetworkErrorNotification(libraryName, error) {
        const alertDiv = document.createElement('div');
        alertDiv.className = 'alert alert-warning alert-dismissible fade show position-fixed';
        alertDiv.style.cssText = `
            top: 20px;
            right: 20px;
            z-index: 9999;
            max-width: 400px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        `;
        
        alertDiv.innerHTML = `
            <div class="d-flex align-items-start">
                <i class="fas fa-exclamation-triangle text-warning me-2 mt-1"></i>
                <div class="flex-grow-1">
                    <h6 class="alert-heading mb-1">⚠️ 검증 오류</h6>
                    <p class="mb-1">${libraryName} 라이브러리 검증 중 오류가 발생했습니다.</p>
                    <small class="text-muted">${error.message}</small>
                </div>
                <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
            </div>
        `;
        
        document.body.appendChild(alertDiv);
        
        setTimeout(() => {
            if (alertDiv.parentNode) {
                alertDiv.remove();
            }
        }, 8000);
    }

    /**
     * 다중 라이브러리 일괄 검증
     * 
     * @param {Array<{name: string, version?: string}>} libraries 라이브러리 목록
     * @returns {Promise<boolean>} 모든 라이브러리 허용 여부
     */
    static async validateMultipleLibraries(libraries) {
        console.log('🔍 다중 라이브러리 검증 시작:', libraries);
        
        try {
            const validationPromises = libraries.map(lib => 
                this.validateLibrary(lib.name, lib.version, { batch: true })
            );
            
            await Promise.all(validationPromises);
            console.log('✅ 모든 라이브러리 검증 통과');
            return true;
            
        } catch (error) {
            console.error('❌ 라이브러리 검증 실패:', error);
            throw error;
        }
    }

    /**
     * 개발 모드에서 검증 우회 (개발용)
     * 
     * @returns {boolean} 개발 모드 여부
     */
    static isDevelopmentMode() {
        return process.env.NODE_ENV === 'development' || 
               window.location.hostname === 'localhost' ||
               window.location.hostname === '127.0.0.1';
    }

    /**
     * 검증 캐시 (동일한 라이브러리 중복 검증 방지)
     */
    static validationCache = new Map();
    static cacheTimeout = 5 * 60 * 1000; // 5분

    /**
     * 캐시된 검증 결과 확인
     * 
     * @param {string} libraryName 라이브러리 이름
     * @param {string|null} version 버전
     * @returns {boolean|null} 캐시된 결과 (없으면 null)
     */
    static getCachedValidation(libraryName, version) {
        const key = `${libraryName}@${version || 'latest'}`;
        const cached = this.validationCache.get(key);
        
        if (cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
            console.log(`📋 캐시된 검증 결과 사용: ${key}`, cached.allowed);
            return cached.allowed;
        }
        
        return null;
    }

    /**
     * 검증 결과 캐시 저장
     * 
     * @param {string} libraryName 라이브러리 이름
     * @param {string|null} version 버전
     * @param {boolean} allowed 허용 여부
     */
    static setCachedValidation(libraryName, version, allowed) {
        const key = `${libraryName}@${version || 'latest'}`;
        this.validationCache.set(key, {
            allowed,
            timestamp: Date.now()
        });
    }
}

export default MobileCodeInterceptor;
